import { getCurrentProductCode } from "@common/api/productContext";
import { captureSentryDiagnosticEvent, logSentryEvent } from "./sentry";
import { getRouteBehavior } from "../routeBehavior";

type AdminWritePhase =
  | "started"
  | "succeeded"
  | "failed"
  | "blocked"
  | "cancelled"
  | "handed_off"
  | "post_write_failed";

type AdminWriteAttributes = Record<string, unknown> & { area: string; action: string };
type AdminWriteFlowContext = {
  route_kind: string;
  origin_route: string;
  current_state: AdminWritePhase;
  transition_reason: string;
  has_workspace_id: boolean;
  has_entity_id: boolean;
};

const getOriginRoute = () => {
  try {
    const pathname = typeof window === "undefined" ? "" : window.location.pathname;
    return {
      route_kind: pathname ? getRouteBehavior(pathname).pageKind : "unknown",
      origin_route: pathname,
    };
  } catch {
    return { route_kind: "unknown", origin_route: "" };
  }
};

const getFlowContext = (phase: AdminWritePhase, attributes: AdminWriteAttributes): AdminWriteFlowContext => ({
  ...getOriginRoute(),
  ...(typeof attributes.route_kind === "string" ? { route_kind: attributes.route_kind } : {}),
  ...(typeof attributes.origin_route === "string" ? { origin_route: attributes.origin_route } : {}),
  // These describe the write lifecycle, not an inferred page/UI state.
  current_state: phase,
  transition_reason: String(attributes.block_reason ?? attributes.cancel_reason
    ?? attributes.failure_stage ?? attributes.handoff_type ?? `${attributes.action}_${phase}`),
  has_workspace_id: Boolean(attributes.workspace_id),
  has_entity_id: Boolean(attributes.entity_id),
});

const getTrackingTime = () => {
  try {
    return globalThis.performance?.now?.() ?? Date.now();
  } catch {
    return Date.now();
  }
};

export const createOperationId = () => {
  try {
    return (
      globalThis.crypto?.randomUUID?.() ??
      `event-${Date.now()}-${Math.random().toString(36).slice(2)}`
    );
  } catch {
    return `event-${Date.now()}-${Math.random().toString(36).slice(2)}`;
  }
};

export const getTrackingErrorCode = (error: unknown) => {
  try {
    const candidate = error as {
      code?: unknown;
      response?: { status?: unknown; data?: { code?: unknown } };
    } | null;
    const value =
      candidate?.response?.data?.code ??
      candidate?.code ??
      candidate?.response?.status;

    return value == null ? "unknown" : String(value).slice(0, 100);
  } catch {
    return "unknown";
  }
};

export const logAdminWrite = (
  phase: AdminWritePhase,
  attributes: AdminWriteAttributes,
) => {
  try {
    const event = `admin.write.${phase}`;
    const normalizedAttributes = {
      ...attributes,
      ...getFlowContext(phase, attributes),
      product_code: getCurrentProductCode(),
      operation_id: attributes.operation_id || createOperationId(),
    };
    const level =
      phase === "failed" || phase === "blocked" || phase === "post_write_failed"
        ? "warn"
        : "info";
    logSentryEvent(event, normalizedAttributes, level);
    if (level === "warn") {
      captureSentryDiagnosticEvent(event, normalizedAttributes, level);
    }
  } catch {
    // Telemetry must never interrupt the user action it observes.
  }
};

export const startAdminWrite = (attributes: AdminWriteAttributes) => {
  const operation = {
    ...attributes,
    ...getOriginRoute(),
    operation_id: createOperationId(),
  };
  const startedAt = getTrackingTime();
  let hasTerminalEvent = false;
  let pendingRefreshes = 0;
  let successAttributes: Record<string, unknown> | undefined;
  logAdminWrite("started", operation);

  const finish = (
    phase: Exclude<AdminWritePhase, "started">,
    extra: Record<string, unknown>,
  ) => {
    if (hasTerminalEvent) {
      return;
    }
    hasTerminalEvent = true;
    logAdminWrite(phase, {
      ...operation,
      ...extra,
      route_kind: operation.route_kind,
      origin_route: operation.origin_route,
      operation_id: operation.operation_id,
      duration_ms: Math.round(getTrackingTime() - startedAt),
    });
  };

  return {
    // Observe an already-started refresh without changing the caller's wait chain.
    observeRefresh: (promise: Promise<unknown>, stage: string, extra: Record<string, unknown> = {}) => {
      pendingRefreshes += 1;
      void promise.then(
        () => {
          pendingRefreshes -= 1;
          if (pendingRefreshes === 0 && successAttributes) finish("succeeded", successAttributes);
        },
        (error) => {
          pendingRefreshes -= 1;
          finish("post_write_failed", {
            ...extra,
            mutation_succeeded: true,
            failure_stage: stage,
            error_code: getTrackingErrorCode(error),
          });
        },
      );
    },
    succeeded: (extra: Record<string, unknown> = {}) =>
      pendingRefreshes > 0 ? (successAttributes = extra) : finish("succeeded", extra),
    failed: (error: unknown, extra: Record<string, unknown> = {}) =>
      finish("failed", {
        ...extra,
        error_code: getTrackingErrorCode(error),
      }),
    blocked: (blockReason: string, extra: Record<string, unknown> = {}) =>
      finish("blocked", { ...extra, block_reason: blockReason }),
    cancelled: (extra: Record<string, unknown> = {}) =>
      finish("cancelled", extra),
    handedOff: (extra: Record<string, unknown> = {}) =>
      finish("handed_off", extra),
    postWriteFailed: (error: unknown, extra: Record<string, unknown> = {}) =>
      finish("post_write_failed", {
        ...extra,
        mutation_succeeded: true,
        error_code: getTrackingErrorCode(error),
      }),
  };
};
